// react
import React, { useState, useEffect } from "react";

// react-router-dom
import { useLocation } from "react-router-dom";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  useMediaQuery,
  Box,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
  TextField,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  FormGroup,
  Switch,
} from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";

// components
import { LinearLoadingComponent } from "components/loading/LoadingComponent";
import { Puller } from "components/common/Puller";
import { NewCertConfirm, NewRequestConfirm } from "components/app/AppFeedback";

// lib/safetwin
import { decryptSeedByPasswordHash4Ed25519 } from "lib/safetwin/crypto/cryptoseed";

// artifacts
import genInfoRecord from "artifacts/info/core/genInfoRecord";

// features/databox
import sendCertificationRequest from "features/databox/sendCertificationRequest";
import completeCertificationRequest from "features/databox/completeCertificationRequest";

// A ---------------------------------------------------------------------- M

const getButtonStyle = (action) => ({
  py: 0.5,
  borderColor: "divider",
  color: action === "close" ? "error.main" : "primary.main",
  textTransform: "none",
  boxShadow: "none",
  borderRadius: 10,
  "&:hover": {
    borderColor: action === "close" ? "error.dark" : "primary.dark",
    backgroundColor: "action.hover",
  },
});

const InfoGenerator = ({ tags, tag, types, setTypes, open, setOpen, handleOpenCertificationSuccessful, handleOpenCertificationError, request, setRequest }) => {
  const { user } = UserAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [keypair, setKeyPair] = useState(null);
  const [text, setText] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [tagSelectionOpen, setTagSelectionOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [approval, setApproval] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (open) handleReset();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open]);

  useEffect(() => {
    const decrypt = () => {
      if (open) {
        const keypair = decryptSeedByPasswordHash4Ed25519(user.reloadUserInfo.passwordHash);
        setKeyPair(keypair);
      }
    };

    decrypt();
  }, [open]);

  useEffect(() => {
    if (request) {
      setText(request.data.text);
      setIsPublic(request.public);
    } else {
      setText("");
      setIsPublic(false);
    }
  }, [request]);

  const handleGenerate = async () => {
    setConfirm(false);
    setIsLoading(true);

    if (keypair) {
      try {
        let result = null;
        const dataBody = { text };

        if (tag) {
          result = await genInfoRecord(isPublic, user.uid, keypair, dataBody, tag);
        } else if (selectedTag) {
          result = await genInfoRecord(isPublic, user.uid, keypair, dataBody, selectedTag);
        } else {
          result = await genInfoRecord(isPublic, user.uid, keypair, dataBody);
        }

        if (result.success) {
          if (request) {
            await completeCertificationRequest(tag, request.id);
          }

          handleOpenCertificationSuccessful();

          if (types && !types.includes("info")) {
            setTypes([...types, "info"]);
          }
        } else {
          handleOpenCertificationError();
        }
      } catch (error) {
        console.error("Error in handleGenerate:", error);
        handleOpenCertificationError();
      } finally {
        setIsLoading(false);
        handleReset();
      }
    } else {
      console.log("No keypair provided.");
    }

    setIsLoading(false);
    handleReset();
  };

  const handleSendApprovalRequest = async () => {
    setApproval(false);
    setIsLoading(true);

    const requestData = { text };
    const requestTag = tag || selectedTag;
    await sendCertificationRequest("info", user.uid, isPublic, requestData, requestTag);

    setIsLoading(false);
    handleReset();
  };

  const handleReset = () => {
    setText("");
    setSelectedTag("");
    setIsPublic(false);

    if (request) {
      setRequest();
    }

    setOpen(false);
  };

  const handleChange = (e) => {
    setSelectedTag(e.target.value);
  };

  const handleOpen = () => {
    setTagSelectionOpen(true);
    window.history.pushState(null, "");
  };

  const handleClose = () => {
    setTagSelectionOpen(false);
  };

  const DialogTitleContent = () => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item>
        <Typography variant="h6" fontWeight="bold">
          {t("certify_note")}
        </Typography>
      </Grid>
      <Grid item>
        <Tooltip title={t("close")} placement="top">
          <IconButton onClick={handleReset} color="error" edge="end">
            <CloseOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );

  const InfoReportForm = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField margin="dense" id="text" name="text" label={t("text")} required variant="outlined" fullWidth value={text} onChange={(e) => setText(e.target.value)} disabled={request} />
      </Grid>
      {location.pathname === "/my-certifications" && tags && (
        <Grid item xs={12}>
          <Typography gutterBottom>{t("databox")}</Typography>
          <FormControl fullWidth>
            <Select
              id="selected-tag-info"
              value={selectedTag}
              displayEmpty
              onChange={handleChange}
              open={tagSelectionOpen}
              onOpen={handleOpen}
              onClose={handleClose}
              sx={{ width: isMobile ? "100%" : "50%" }}
            >
              <MenuItem value="">
                <Typography color="gray">{t("no_databox")}</Typography>
              </MenuItem>
              {tags
                .sort((a, b) => a.nickname.localeCompare(b.nickname))
                .map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.nickname}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item container xs={12}>
        <FormGroup>
          <FormControlLabel
            control={<Switch checked={isPublic} onChange={(event) => setIsPublic(event.target.checked)} disabled={request} inputProps={{ "aria-label": "controlled" }} />}
            label={t("mark_as_public")}
          />
        </FormGroup>
      </Grid>
    </Grid>
  );

  return (
    <>
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={handleReset}
          onOpen={() => setOpen(true)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              height: "80%",
              borderTopLeftRadius: "4%",
              borderTopRightRadius: "4%",
            },
          }}
        >
          <Puller />
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" textAlign="center" mt={2} mb={3}>
              {t("certify_note")}
            </Typography>
            {isLoading ? (
              <LinearLoadingComponent />
            ) : (
              <>
                {InfoReportForm}
                <Grid container spacing={1} mt={5}>
                  <Grid item xs={12}>
                    <Button fullWidth variant="outlined" sx={getButtonStyle("close")} onClick={handleReset}>
                      {t("cancel")}
                    </Button>
                  </Grid>
                  {!request && (
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        sx={getButtonStyle("open")}
                        disabled={isLoading || (location.pathname === "/my-certifications" && (!text || !selectedTag)) || (location.pathname !== "/my-certifications" && !text)}
                        onClick={() => setApproval(true)}
                      >
                        {t("send_request")}
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Button fullWidth variant="outlined" sx={getButtonStyle("open")} disabled={isLoading || !text} onClick={() => setConfirm(true)}>
                      {t("certify")}
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </SwipeableDrawer>
      ) : (
        <Dialog
          open={open}
          onClose={handleReset}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiPaper-root": {
              borderRadius: 2,
              p: 2,
              boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <DialogTitle>
            <DialogTitleContent />
          </DialogTitle>
          {isLoading ? (
            <LinearLoadingComponent />
          ) : (
            <>
              <DialogContent>{InfoReportForm}</DialogContent>
              <DialogActions>
                <Button variant="outlined" sx={getButtonStyle("close")} onClick={handleReset}>
                  {t("cancel")}
                </Button>
                {!request && (
                  <Button
                    variant="outlined"
                    sx={getButtonStyle("open")}
                    disabled={isLoading || (location.pathname === "/my-certifications" && (!text || !selectedTag)) || (location.pathname !== "/my-certifications" && !text)}
                    onClick={() => setApproval(true)}
                  >
                    {t("send_request")}
                  </Button>
                )}
                <Button variant="outlined" sx={getButtonStyle("open")} disabled={isLoading || !text} onClick={() => setConfirm(true)}>
                  {t("certify")}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      )}
      <NewCertConfirm confirm={confirm} setConfirm={setConfirm} handleGenerate={handleGenerate} />
      <NewRequestConfirm approval={approval} setApproval={setApproval} handleSendApprovalRequest={handleSendApprovalRequest} />
    </>
  );
};

export default InfoGenerator;
